// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';
import { isTouch } from '@trello/browser';
// eslint-disable-next-line no-restricted-imports
import { Controller } from 'app/scripts/controller';
import { PopOver } from 'app/scripts/views/lib/PopOver';
import { PostRender } from 'app/scripts/views/lib/PostRender';
import { Analytics, tracingCallback } from '@trello/atlassian-analytics';
const {
  setIntervalOnAnimationFrame,
  throttle,
  throttleOnAnimationFrame,
} = require('@trello/time');
const pointHorizontallyInRect = ({ x }, { left, right }) =>
  left <= x && x < right;

const inset = ({ top, left, bottom, right }, horizontal, vertical) => ({
  top: top + vertical,
  bottom: bottom - vertical,
  left: left + horizontal,
  right: right - horizontal,
});
const getMousePosition = (e) => ({
  x: e.pageX,
  y: e.pageY,
});
const concat = (...fns) =>
  function () {
    for (const fn of fns) {
      if (fn) {
        fn.apply(this, arguments);
      }
    }
  };
const prefix = (fn, prefix) => concat(prefix, fn);

// eslint-disable-next-line @trello/enforce-variable-case
const DragSort = {};

class DragInfo {
  constructor({
    scrollBoard,
    scrollLists,
    scrollCard,
    commitEvent,
    cancelMethod,
  }) {
    this.hasMousedLeft = false;
    this.hasMousedRight = false;
    this.stopped = false;
    this.aborted = false;

    this.scrollBoard = scrollBoard;
    this.scrollLists = scrollLists;
    this.scrollCard = scrollCard;
    this.commitEvent = commitEvent;
    this.cancelMethod = cancelMethod;
    this._queue = [];
  }

  start(e, ui) {
    this.$helper = ui.helper;
    this.$target = $(e.target);
    this.initialMousePosition = getMousePosition(e);
    this.scrollTimer = null;

    this.lockModelCache();
    this.startTrackingMouse();
    this.hidePopoversForSomeReason();

    this.boardElement = document.querySelector('#board');
    this.boardCanvasElement = document.querySelector('.board-canvas');
    this.windowOverlayElement = document.querySelector('.window-overlay');

    this.cachedHoveredList = this.getHoveredListForPoint(
      this.initialMousePosition,
    );
    if (this.scrollLists || this.scrollBoard || this.scrollCard) {
      this.startAutoscrolling();
    }

    DragSort.sorting = true;
    DragSort.isScrolling = false;
    DragSort.$target = this.$target;
    this.onStop(() =>
      // This fixes a timing issue where cards sometimes open after dragging.
      // If sorting is cleared too early, the card click will go through.
      // Observed in Firefox.
      // Obviously there are better ways to fix this.
      window.requestAnimationFrame(() => (DragSort.sorting = false)),
    );
  }

  lockModelCache() {
    const modelCache = Controller.getCurrentModelCache();
    if (!modelCache) {
      throw new Error('Attempt to use DragInfo on a page with no modelCache!');
    }
    const _lockIndex = modelCache.lock('DragSort.startSort');
    return this.onStop(() => modelCache.unlock(_lockIndex));
  }

  hidePopoversForSomeReason() {
    // If you're dragging a member from the "N observers" section,
    // we don't want to hide this.
    if (this.$target.closest('.pop-over').length === 0) {
      PopOver.hide();
    }
  }

  startAutoscrolling() {
    const scrollIntervalFunc = setIntervalOnAnimationFrame(
      this.autoscroll.bind(this),
      16,
    );
    return this.onStop(() => scrollIntervalFunc.clear());
  }

  getHoveredListForPoint(point) {
    for (const el of document.querySelectorAll('.js-list')) {
      if (pointHorizontallyInRect(point, el.getBoundingClientRect())) {
        return el.querySelector('.list-cards');
      }
    }
    return null;
  }

  startTrackingMouse() {
    const updateHoveredList = throttle((point) => {
      // Because this is throttled, it's possible that it'll fire
      // after the drag has stopped. That would be madness.
      if (this.stopped) {
        return;
      }
      return (this.cachedHoveredList = this.getHoveredListForPoint(point));
    }, 100);

    const tolerance = 20;

    // this method handles autoscroll updates and
    // hovered list bounding box updates
    const throttledOnMouseMove = throttleOnAnimationFrame((e) => {
      const mousePosition = getMousePosition(e);
      if (mousePosition.x < this.initialMousePosition.x - tolerance) {
        this.hasMousedLeft = true;
      }
      if (mousePosition.x > this.initialMousePosition.x + tolerance) {
        this.hasMousedRight = true;
      }
      updateHoveredList(mousePosition);
    });

    $(document).on('mousemove.dragsort', throttledOnMouseMove);

    return this.onStop(() =>
      $(document).off('mousemove.dragsort', throttledOnMouseMove),
    );
  }

  onStop(fn) {
    return this._queue.push(fn);
  }

  stop(e, ui) {
    this.stopped = true;

    for (const fn of this._queue) {
      fn.call(this);
    }
    delete this._queue;

    if (!this.aborted) {
      this.$target.trigger(this.commitEvent, ui);
    }
  }

  scrollDeltaForOverhang(overhang) {
    return overhang * 0.1;
  }

  // will set isScrolling to true and set a timer to set it to false,
  // so that public consumers can see if we are autoscrolling through cards or not.
  enqueuePublicScrolling() {
    DragSort.isScrolling = true;
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      DragSort.isScrolling = false;
    }, 50);
  }

  autoscrollBoard() {
    const { left, right } = this.$helper[0].getBoundingClientRect();
    const scrollFrame = inset(
      this.boardCanvasElement.getBoundingClientRect(),
      100,
      0,
    );

    const leftOverhang = scrollFrame.left - left;
    if (leftOverhang > 0 && this.hasMousedLeft) {
      this.boardElement.scrollLeft -= this.scrollDeltaForOverhang(leftOverhang);
    }

    const rightOverhang = right - scrollFrame.right;
    if (rightOverhang > 0 && this.hasMousedRight) {
      this.boardElement.scrollLeft += this.scrollDeltaForOverhang(
        rightOverhang,
      );
    }
  }

  autoscrollLists() {
    const { top, bottom } = this.$helper[0].getBoundingClientRect();
    const scrollFrame = inset(
      this.cachedHoveredList.getBoundingClientRect(),
      0,
      50,
    );

    const topOverhang = scrollFrame.top - top;
    if (topOverhang > 0) {
      this.enqueuePublicScrolling();
      this.cachedHoveredList.scrollTop -= this.scrollDeltaForOverhang(
        topOverhang,
      );
    }

    const bottomOverhang = bottom - scrollFrame.bottom;
    if (bottomOverhang > 0) {
      this.enqueuePublicScrolling();
      this.cachedHoveredList.scrollTop += this.scrollDeltaForOverhang(
        bottomOverhang,
      );
    }
  }

  autoscrollCard() {
    let { bottom } = this.$helper[0].getBoundingClientRect();
    const { top } = this.$helper[0].getBoundingClientRect();

    // Some draggables, like checklists, have the potential to be really tall
    // and would be more likely to start out overhanging the bottom
    const MAX_HELPER_HEIGHT = 64;
    if (bottom - top > MAX_HELPER_HEIGHT) {
      bottom = top + MAX_HELPER_HEIGHT;
    }

    const scrollFrame = inset(
      this.windowOverlayElement.getBoundingClientRect(),
      0,
      100,
    );

    const topOverhang = scrollFrame.top - top;
    if (topOverhang > 0) {
      this.windowOverlayElement.scrollTop -= this.scrollDeltaForOverhang(
        topOverhang,
      );
    }

    const bottomOverhang = bottom - scrollFrame.bottom;
    if (bottomOverhang > 0) {
      this.windowOverlayElement.scrollTop += this.scrollDeltaForOverhang(
        bottomOverhang,
      );
    }
  }

  autoscroll() {
    if (this.scrollBoard) {
      this.autoscrollBoard();
    }
    if (this.scrollLists && this.cachedHoveredList) {
      this.autoscrollLists();
    }
    if (this.scrollCard) {
      this.autoscrollCard();
    }
  }

  abort() {
    const manager = $.ui.ddmanager.current;
    this.aborted = true;
    // Work around a bug in jQueryUI Sortable
    // If the drag manager thinks that the element has switched sortable
    // containers, it will fire a receive event on the new container, even
    // though the drag has been cancelled
    if (manager) {
      manager.currentContainer = manager;
    }

    return this.$target[this.cancelMethod]('cancel');
  }
}

let currentDragInfo = null;

const normalizeOpts = function (customOpts, opts) {
  opts = {
    ...opts,
    distance: 7,
    scroll: false,
  };

  // This allows you to drag cards over the "Add a card..." button at the
  // bottom of lists (even though that isn't inside their destination).
  if (customOpts.scrollLists) {
    opts.custom = {
      refreshContainers() {
        this.containers = this.containers
          .filter((container) => $.contains(document, container.element[0]))
          .map((container) => {
            const el = container.element.closest('.js-list')[0];
            const frame = el.getBoundingClientRect();
            for (const field of ['left', 'top', 'width', 'height']) {
              container.containerCache[field] = frame[field];
            }
            return container;
          });
      },
    };
  }

  opts.start = prefix(opts.start, function (e, ui) {
    if (currentDragInfo) {
      throw new Error(
        'Attempt to start a drag event while another is in progress!',
      );
    }
    currentDragInfo = new DragInfo(customOpts);
    currentDragInfo.start(e, ui);
  });

  opts.stop = prefix(opts.stop, function (e, ui) {
    e?.stopPropagation();
    currentDragInfo.stop(e, ui);
    currentDragInfo = null;
  });

  return opts;
};

const afterDragCompletes = (fn) => {
  if (currentDragInfo) {
    currentDragInfo.onStop(fn);
  } else {
    fn();
  }
};

const defaultCalcSize = ($item) => ({
  width: $item.outerWidth(),
  height: $item.outerHeight(),
});

const hookSortable = function (selector, customOpts, opts) {
  if (isTouch()) {
    return;
  }
  // eslint-disable-next-line @trello/enforce-variable-case
  let $elements = null;

  const shouldNormalizeAppendTo =
    !('helper' in opts) || opts.helper === 'original';

  opts.start = prefix(opts.start, function (e, ui) {
    if (shouldNormalizeAppendTo && 'appendTo' in opts) {
      ui.helper.appendTo(opts.appendTo);
      $elements.sortable('refreshPositions', true);
    }

    const calcSize = customOpts.calcSize || defaultCalcSize;
    const { width, height } = calcSize(ui.item);

    ui.placeholder.width(width);
    ui.placeholder.height(height);
  });

  customOpts = {
    ...customOpts,
    commitEvent: 'sortcommit',
    cancelMethod: 'sortable',
  };
  opts = normalizeOpts(customOpts, opts);

  PostRender.enqueue(() =>
    // Don't re-initialize a sortable while a drag is in progress;
    // the sortable replaces the initial placeholder with an object
    // at the start of a drag, and calling .sortable will set the
    // placeholder back to a string, causing unexpected behavior for
    // the drag in progress
    afterDragCompletes(() => {
      $elements = $(selector);
      $elements.sortable(opts);
    }),
  );
};

const hookDraggable = function (selector, customOpts, opts) {
  if (isTouch()) {
    return;
  }
  customOpts = {
    ...customOpts,
    commitEvent: 'dragcommit',
    cancelMethod: 'draggable',
  };
  opts = normalizeOpts(customOpts, opts);
  PostRender.enqueue(() => $(selector).draggable(opts));
};

DragSort.refreshListSortable = () => {
  return hookSortable(
    '.js-list-sortable',
    {
      scrollBoard: true,
      scrollLists: false,
      calcSize($listWrapper) {
        // eslint-disable-next-line @trello/enforce-variable-case
        const $el = $listWrapper.children('.list');
        return {
          width: $el.outerWidth(),
          height: $el.outerHeight(),
        };
      },
    },
    {
      handle: '.list-header',
      tolerance: 'pointer',
      placeholder: 'list-wrapper placeholder',
      items: '.js-list',
      appendTo: '#trello-root',
    },
  );
};

DragSort.refreshCalendarCardSortable = () => {
  return hookSortable(
    '.js-calendar-sortable',
    {
      scrollBoard: false,
      scrollLists: false,
    },
    {
      connectWith: '.js-calendar-sortable',
      placeholder: 'list-card placeholder-none',
      items:
        '.list-card:not(.placeholder-none, .hide, .js-composer), .js-draggable-calendar-check-item',
      helper: 'clone',
      appendTo: '#trello-root',

      over(e, ui) {
        return $(e.target).closest('.calendar-day').addClass('drop');
      },
      out(e, ui) {
        return $(e.target).closest('.calendar-day').removeClass('drop');
      },
    },
  );
};

DragSort.refreshListCardSortable = () => {
  return hookSortable(
    '.js-sortable',
    {
      scrollBoard: true,
      scrollLists: true,
    },
    {
      connectWith: '.js-sortable:not(.card-limits-full)',
      placeholder: 'list-card placeholder',
      tolerance: 'pointer',
      items:
        '.list-card:not(.placeholder, .hide, .js-composer, .js-out-of-viewport)',
      appendTo: '#trello-root',
    },
  );
};

DragSort.refreshDraggableBoardMembers = () => {
  return hookDraggable(
    `.js-list-draggable-board-members \
.member:not(.js-member-deactivated)`,
    {
      scrollBoard: true,
      scrollLists: true,
    },
    {
      revert: 'invalid',
      revertDuration: 150,
      appendTo: '#trello-root',
      zIndex: 100,
      helper: 'clone',
      start(e, ui) {
        Analytics.sendUIEvent({
          action: 'dragged',
          actionSubject: 'member',
          source: 'boardScreen',
          containers: {
            board: {
              id: Controller.getCurrentBoardView()?.model.id,
            },
          },
        });

        // droppable.hoverClass in card.js is going to add an active class
        // so we don't want it to look like there are two active cards
        $('.list-card.active-card').removeClass('active-card');
      },
    },
  );
};

DragSort.refreshDraggableCardMembers = () => {
  return hookDraggable(
    `.js-list-draggable-card-members \
.member:not(.js-member-deactivated)`,
    {
      scrollBoard: true,
      scrollLists: true,
    },
    {
      appendTo: '#trello-root',
      zIndex: 100,
      helper: 'clone',
      start(e, ui) {
        Analytics.sendUIEvent({
          action: 'dragged',
          actionSubject: 'member',
          source: 'boardScreen',
          containers: {
            board: {
              id: Controller.getCurrentBoardView()?.model.id,
            },
          },
        });

        // droppable.hoverClass in card.js is going to add an active class
        // so we don't want it to look like there are two active cards
        $('.list-card.active-card').removeClass('active-card');
      },
      stop(e, ui) {
        // Card member was dragged off of
        const sourceCardPath = e.target.closest('.list-card').href.split('/');
        const sourceCardShortLink = sourceCardPath[sourceCardPath.length - 2];
        const sourceCard = Controller.getCurrentModelCache().get(
          'Card',
          sourceCardShortLink,
        );
        // Remove from source card if member was not dragged onto any card or dragged onto another card
        if (
          !ui.helper.data('droppedOnCard') ||
          ui.helper.data('droppedOnCard').id !== sourceCard.id
        ) {
          const memberId = ui.helper.data('idmem');

          const source = 'boardScreen';
          const traceId = Analytics.startTask({
            taskName: 'edit-card/idMembers',
            source,
          });

          sourceCard.removeMemberWithTracing(
            memberId,
            traceId,
            tracingCallback(
              {
                taskName: 'edit-card/idMembers',
                traceId,
                source,
              },
              () => {
                Analytics.sendUpdatedCardFieldEvent({
                  field: 'idMembers',
                  source,
                  containers: {
                    card: { id: sourceCard.id },
                    board: { id: sourceCard.get('idBoard') },
                    list: { id: sourceCard.get('idList') },
                  },
                  attributes: {
                    taskId: traceId,
                    changeType: 'remove member',
                  },
                });
              },
            ),
          );
        }
      },
    },
  );
};

DragSort.refreshDraggableStickers = () => {
  return hookDraggable(
    '.js-draggable-sticker:not(.disabled)',
    {
      scrollBoard: true,
      scrollLists: true,
    },
    {
      revert: 'invalid',
      revertDuration: 150,
      appendTo: '#trello-root',
      zIndex: 100,
      helper: 'clone',
      start(e, ui) {
        Analytics.sendUIEvent({
          action: 'dragged',
          actionSubject: 'sticker',
          source: 'boardScreen',
          containers: {
            board: {
              id: Controller.getCurrentBoardView()?.model.id,
            },
          },
        });
        const randomRotation = Math.random() * 20 - 10;
        ui.helper.find('.sticker-select-fixed').css({
          transform: `rotate(${randomRotation}deg)`,
          '-webkit-transform': `rotate(${randomRotation}deg)`,
        });
      },
    },
  );
};

DragSort.refreshCardSortable = ($el, opts) => {
  return hookSortable($el, { scrollCard: true }, opts);
};

DragSort.abort = () => {
  if (!currentDragInfo) {
    throw new Error('Abort called while not dragging!');
  }
  return currentDragInfo.abort();
};

DragSort.refreshIfInitialized = ($el) => {
  // The first drag after a card add fails in jQuery 2.0.0
  if ($el && $el.hasClass('ui-sortable')) {
    return $el.sortable('refresh');
  }
};

export { DragSort };
